import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  BookOpen,
  Bot,
  Calendar,
  Clock,
  CreditCard,
  FileText,
  GraduationCap,
  Settings,
  Users,
} from 'lucide-react';
import { Header } from './Header';
import { MobileNav } from './MobileNav';
import { SidebarShareCard } from './SidebarShareCard';
import { cn } from '@/lib/utils';
import { useBookingRequests } from '@/hooks/use-booking-requests';
import { useMyProfile } from '@/hooks/use-caregiver-profile';
import { useAppFeatures } from '@/hooks/use-app-features';
import {
  preloadCalendar,
  preloadBookings,
  preloadManagePlan,
  preloadShiftNotes,
} from '@/lib/preload';
import { hasActiveSubscription } from '@/lib/subscription';
import { buildCaregiverProfileLink } from '@/lib/share';
import { useAuthStore } from '@/store/auth-store';
import type { ServiceType } from '@/types/profile';
import { WarningBanner, type WarningBannerType } from '@/components/common/WarningBanner';

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  showDot?: boolean;
  requiresActiveSubscription?: boolean;
}

const ELDER_COMPANION_SERVICE: ServiceType = 'COMPANION_SERVICES';

export function DashboardLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuthStore();
  const { enableEngagementLibrary, enableProBookings } = useAppFeatures();
  const isSupervisor = user?.userRole === 'SUPERVISOR';
  const isAgencySupervisor = isSupervisor && user?.supervisorType === 'AGENCY';
  const isAgencyCaregiver =
    user?.userRole === 'CARE_GIVER' && user.supervisorContext?.supervisorType === 'AGENCY';
  const supervisorCaregiversLabel =
    user?.supervisorType === 'GUARDIAN' ? 'Teen Caregivers' : 'Caregivers';
  const isSubscriptionActive = hasActiveSubscription(user?.subscription);
  const shouldLoadCaregiverProfile = !isSupervisor && !isAgencyCaregiver;
  const { profile } = useMyProfile({ enabled: shouldLoadCaregiverProfile });
  const { requests = [] } = useBookingRequests({
    enabled: !isSupervisor && !isAgencyCaregiver && isSubscriptionActive,
  });
  const now = new Date();
  const hasPendingRequest = requests.some(request => {
    if (request.status !== 'REQUESTED') {
      return false;
    }
    return new Date(request.requestedStartTime) > now;
  });

  // Prevent document-level scrolling when dashboard is mounted
  useEffect(() => {
    document.body.classList.add('dashboard-layout');
    return () => {
      document.body.classList.remove('dashboard-layout');
    };
  }, []);

  const hasElderCompanionService =
    enableEngagementLibrary &&
    shouldLoadCaregiverProfile &&
    (profile?.rolesForHire?.includes(ELDER_COMPANION_SERVICE) ?? false);

  const warningBannerType: WarningBannerType | undefined =
    shouldLoadCaregiverProfile && profile?.profileLinkAccess === 'DISABLED'
      ? 'PROFILE_DISABLED'
      : undefined;

  const supervisorNavItems: NavItem[] = [
    {
      label: supervisorCaregiversLabel,
      path: '/dashboard/caregivers',
      icon: Users,
      requiresActiveSubscription: true,
    },
    ...(isAgencySupervisor
      ? [
          {
            label: 'Engagement Library',
            path: '/dashboard/engagement-library',
            icon: BookOpen,
            requiresActiveSubscription: true,
          },
          {
            label: 'Course',
            path: '/dashboard/course',
            icon: GraduationCap,
            requiresActiveSubscription: true,
          },
          {
            label: 'Caregiver Confidence',
            path: '/dashboard/caregiver-confidence',
            icon: FileText,
            requiresActiveSubscription: true,
          },
          {
            label: 'Cally Coach (AI)',
            path: '/dashboard/cally-coach',
            icon: Bot,
            requiresActiveSubscription: true,
          },
        ]
      : []),
    {
      label: 'Manage Plan',
      path: '/dashboard/manage-plan',
      icon: CreditCard,
    },
  ];

  const caregiverNavItems: NavItem[] = [
    {
      label: 'Calendar',
      path: '/dashboard/calendar',
      icon: Calendar,
      requiresActiveSubscription: true,
    },
    {
      label: enableProBookings ? 'Bookings' : 'Interviews',
      path: '/dashboard/bookings',
      icon: Clock,
      showDot: hasPendingRequest,
      requiresActiveSubscription: true,
    },
    {
      label: 'Clients',
      path: '/dashboard/clients',
      icon: Users,
      requiresActiveSubscription: true,
    },
    ...(hasElderCompanionService
      ? [
          {
            label: 'Engagement Library',
            path: '/dashboard/engagement-library',
            icon: BookOpen,
            requiresActiveSubscription: true,
          },
        ]
      : []),
    // {
    //   label: 'Shift Note',
    //   path: '/dashboard/shift-notes',
    //   icon: MessageSquare,
    //   requiresActiveSubscription: true,
    // },
    // This is temporarily disabled and will be re-enabled just for the agency caregiver use case when that is implemented.
    {
      label: 'Manage Plan',
      path: '/dashboard/manage-plan',
      icon: CreditCard,
    },
    {
      label: 'Profile',
      path: '/dashboard/settings',
      icon: Settings,
    },
  ];

  const agencyCaregiverNavItems: NavItem[] = [
    {
      label: 'Engagement Library',
      path: '/dashboard/engagement-library',
      icon: BookOpen,
      requiresActiveSubscription: true,
    },
    {
      label: 'Course',
      path: '/dashboard/course',
      icon: GraduationCap,
      requiresActiveSubscription: true,
    },
    {
      label: 'Caregiver Confidence',
      path: '/dashboard/caregiver-confidence',
      icon: FileText,
      requiresActiveSubscription: true,
    },
    {
      label: 'Cally Coach (AI)',
      path: '/dashboard/cally-coach',
      icon: Bot,
      requiresActiveSubscription: true,
    },
  ];

  const navItems: NavItem[] = isSupervisor
    ? supervisorNavItems
    : isAgencyCaregiver
      ? agencyCaregiverNavItems
      : caregiverNavItems;

  const profileLink =
    shouldLoadCaregiverProfile && isSubscriptionActive && profile?.profileLinkPath
      ? buildCaregiverProfileLink(profile.profileLinkPath)
      : null;

  // Set up preloading on hover for navigation links
  useEffect(() => {
    const preloadMap: Record<string, () => Promise<any>> = {
      '/dashboard/calendar': preloadCalendar,
      '/dashboard/bookings': preloadBookings,
      '/dashboard/shift-notes': preloadShiftNotes,
      '/dashboard/manage-plan': preloadManagePlan,
    };

    const setupHoverPreload = () => {
      Object.entries(preloadMap).forEach(([path, preloadFn]) => {
        const links = document.querySelectorAll(`a[href="${path}"]`);
        links.forEach(link => {
          let preloaded = false;
          const handleMouseEnter = () => {
            if (!preloaded) {
              preloadFn();
              preloaded = true;
            }
          };
          link.addEventListener('mouseenter', handleMouseEnter);

          // Cleanup function
          return () => {
            link.removeEventListener('mouseenter', handleMouseEnter);
          };
        });
      });
    };

    // Set up preloading after a short delay to ensure DOM is ready
    const timeoutId = setTimeout(setupHoverPreload, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="app-height bg-linen flex flex-col overflow-hidden">
      {/* Warning Message Banner */}
      {warningBannerType && <WarningBanner warningType={warningBannerType} />}

      {/* Header */}
      <Header
        showAuth
        showNav
        onMenuClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen}
      />

      {/* Mobile Navigation */}
      <MobileNav
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navItems={navItems}
        profileLink={profileLink}
      />

      <div className="flex flex-1 min-h-0">
        {/* Desktop Sidebar - Fixed within flex layout, doesn't scroll */}
        <aside
          className="hidden md:flex md:w-64 md:flex-shrink-0 md:max-w-64 bg-[#FFFFFC] border-r border-lilac-900 shadow-xs z-10"
          role="complementary"
          aria-label="Dashboard navigation"
        >
          <div className="flex flex-col flex-1 min-h-0 w-full min-w-0">
            <nav
              className="flex-1 px-4 py-6 space-y-1 overflow-y-auto"
              role="navigation"
              aria-label="Main navigation"
            >
              {navItems.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const hasDotBadge = Boolean(item.showDot);
                const isDisabled = Boolean(
                  item.requiresActiveSubscription && !isSubscriptionActive
                );
                const ariaLabel = hasDotBadge ? `${item.label} (new pending request)` : item.label;

                const baseItemClasses = cn(
                  'flex items-center justify-between px-4 py-3 rounded-lg transition-colors',
                  isDisabled
                    ? 'cursor-not-allowed bg-night-100 text-night-300'
                    : isActive
                      ? 'bg-lilac-500 text-blueviolet'
                      : 'text-night-900 hover:bg-lilac-500 hover:text-blueviolet'
                );

                if (isDisabled) {
                  return (
                    <div
                      key={item.path}
                      className={baseItemClasses}
                      aria-disabled="true"
                      aria-label={ariaLabel}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                        <span className="font-body font-medium">{item.label}</span>
                      </div>
                      {hasDotBadge && (
                        <span
                          className="h-2.5 w-2.5 rounded-full bg-tigerlily"
                          aria-label="New pending request"
                        />
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={baseItemClasses}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={ariaLabel}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                      <span className="font-body font-medium">{item.label}</span>
                    </div>
                    {hasDotBadge && (
                      <span
                        className="h-2.5 w-2.5 rounded-full bg-tigerlily"
                        aria-label="New pending request"
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Footer */}
            <div className="p-4 py-6 space-y-3 overflow-hidden min-w-0">
              {/* Profile Link & Share */}
              {profileLink && <SidebarShareCard profileLink={profileLink} />}
            </div>
          </div>
        </aside>

        {/* Main content - scrolls independently */}
        <main id="main-content" className="flex-1 min-h-0 overflow-y-auto bg-linen" role="main">
          <div className="max-w-7xl mx-auto container-responsive py-4 sm:py-6 lg:py-8">
            <Outlet />
            <div className="h-safe-bottom" aria-hidden="true" />
          </div>
        </main>
      </div>
    </div>
  );
}
